"use client";
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import styles from "./confirm-dialog.module.css";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
} from "@/components/ui";

interface ConfirmOptions {
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  /** Optional third button (e.g. "Save"). When set, choose() can resolve "alt". */
  altLabel?: string;
}

/** Three-way outcome for choose(); confirm() collapses this to a boolean. */
export type ConfirmChoice = "confirm" | "cancel" | "alt";

type Resolver = (value: ConfirmChoice) => void;

interface ConfirmContextValue {
  /** Binary confirm — resolves true only on the confirm button. */
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
  /** Three-way confirm — distinguishes the optional alt button. */
  choose: (options: ConfirmOptions) => Promise<ConfirmChoice>;
}

const ConfirmContext = createContext<ConfirmContextValue>({
  confirm: async () => false,
  choose: async () => "cancel",
});

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [dialog, setDialog] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  const choose = useCallback(
    (options: ConfirmOptions): Promise<ConfirmChoice> => {
      return new Promise<ConfirmChoice>((resolve) => {
        resolverRef.current = resolve;
        setDialog(options);
      });
    },
    [],
  );

  const confirm = useCallback(
    (options: ConfirmOptions | string): Promise<boolean> => {
      const opts = typeof options === "string" ? { message: options } : options;
      return choose(opts).then((c) => c === "confirm");
    },
    [choose],
  );

  function handleClose(value: ConfirmChoice) {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setDialog(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm, choose }}>
      {children}
      <Dialog open={!!dialog} onOpenChange={() => handleClose("cancel")}>
        <DialogContent showClose={false} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Confirm</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className={styles.message}>{dialog?.message}</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => handleClose("cancel")}>
              Cancel
            </Button>
            {dialog?.altLabel && (
              <Button variant="primary" onClick={() => handleClose("alt")}>
                {dialog.altLabel}
              </Button>
            )}
            <Button
              variant={dialog?.danger ? "danger" : "primary"}
              onClick={() => handleClose("confirm")}
              autoFocus
            >
              {dialog?.confirmLabel ?? "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
